import {
  CircleStackIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  RocketLaunchIcon,
  ServerStackIcon,
  WindowIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { AffiliationIcon } from '@/components/icons/Affiliation.icon';
import { ScanType } from '@/sections/asset/types';

/**
 * Capability names used in job configurations
 */
export const CAPABILITIES = {
  PORT_SCAN: 'portscan',
  CRAWLER: 'crawler',
  BURP_DAST_SCAN: 'burp-dast-scan',
  WEBAPP_DISCOVERY: 'webapp-discovery',
  NUCLEI: 'nuclei',
  CALIGULA_AMI: 'caligula-ami',
} as const;

/**
 * Configuration keys for capability jobs
 */
export const CAPABILITY_CONFIG_KEYS = {
  RANDOM_USER_AGENT: 'random-user-agent',
  RUN_TYPE: 'run-type',
} as const;

/**
 * Configuration for a scan type
 */
export interface ScanTypeConfig {
  /** Display label for the scan */
  label: string;
  /** Icon component for the scan */
  icon: React.ComponentType<{ className?: string }>;
  /** Capabilities to execute for this scan type */
  capabilities: string[];
  /** Whether this scan requires WebApplication assets */
  requiresWebApplication?: boolean;
  /** Whether this scan requires network assets */
  requiresNetworkAsset?: boolean;
}

/**
 * Centralized scan type configuration
 * Maps scan types to their display properties and capabilities
 */
export const SCAN_TYPE_CONFIGS: Record<ScanType, ScanTypeConfig> = {
  affiliation: {
    label: 'Confirm Asset Affiliation',
    icon: AffiliationIcon,
    capabilities: [], // Uses special affiliation API
  },
  port: {
    label: 'Port Scan',
    icon: ServerStackIcon,
    capabilities: [CAPABILITIES.PORT_SCAN],
    requiresNetworkAsset: true,
  },
  crawl: {
    label: 'Web Crawl',
    icon: GlobeAltIcon,
    capabilities: [CAPABILITIES.CRAWLER],
    requiresWebApplication: true,
  },
  webscan: {
    label: 'Web Scan',
    icon: WindowIcon,
    capabilities: [CAPABILITIES.BURP_DAST_SCAN],
    requiresWebApplication: true,
  },
  network: {
    label: 'Network Scan',
    icon: ComputerDesktopIcon,
    capabilities: [CAPABILITIES.NUCLEI],
    requiresNetworkAsset: true,
  },
  comprehensive: {
    label: 'Comprehensive Scan',
    icon: RocketLaunchIcon,
    capabilities: [], // Runs multiple scans based on asset type
  },
  ami: {
    label: 'AMI Scan',
    icon: CircleStackIcon,
    capabilities: [CAPABILITIES.CALIGULA_AMI],
  },
};

/**
 * Get all available scan types
 */
export const SCAN_TYPES = Object.keys(SCAN_TYPE_CONFIGS) as ScanType[];
